import { defineStore } from "pinia";
import { reactive, ref, set, del } from "vue";
import { useIQChatStore } from "./iqChatStore";
import configService from "@/services/config";
import store from "@/store";
import { useNotificationStore } from "@/store/pinia/notifications";
import { askIQStream } from "@/api/askIQApi";
import { getCloudflareVideoStatus, confirmProposedVideo } from "@/api/proficiencyAPI";
import {
  changeCheckpointV2,
  saveVideoInspectionInstructions,
  notifyCheckpointPresenceUpdated,
  getCheckpointVideoSignedUrl,
} from "@/api/responsibilityAPI";
import {
  cloudflareStreamIframeHtml,
  cloudflareStreamIframeSrc,
  extractCloudflareStreamVideoUidFromEmbeds,
  normalizeCloudflareStreamVideoUid,
} from "@/utils/simulatedCloudflareStreamWebhook";
import {
  VIDEO_POST_UPLOAD_TOAST_MESSAGE,
  VIDEO_POST_UPLOAD_TOAST_TITLE,
  isPlayableSignedVideoUrl,
  pollCloudflareStreamStatus,
} from "@/utils/videoPostUploadReveal";
import {
  buildVideoToComponentsPayload,
  buildIqVideoSuggestEndpoint,
} from "@/utils/buildVideoToComponentsPayload";
import { buildVideoToComponentsCheckpointPatch } from "@/utils/buildVideoToComponentsCheckpointPatch";
import {
  buildContextValidationSuggestedInstructions,
  isContextValidationFail,
  isContextVerificationFailed,
  VERIFICATION_FAILED_DEFAULT_ERROR,
  VIDEO_CONTEXT_STREAM_INTERRUPTED_ERROR,
  buildClearContextValidationMismatchPatch,
} from "@/utils/buildContextValidationSuggestedInstructions";

/** Gateway / proxy statuses that mean the stream was cut short, not that IQ failed the check. */
const IQ_STREAM_INTERRUPTED_HTTP_STATUSES = [499, 502, 503, 504];

/** Max-wait for the context-validation IQ SSE stream (mirrors the editor constant). */
const IQ_STREAM_TIMEOUT_MS = 360000;

/** Every per-checkpoint record owns these keys up-front so reactive writes always land. */
function makeRecord() {
  return {
    /** null | 'processing' | 'validating' | 'mismatch' | 'verification-failed' */
    phase: null,
    revealPending: false,
    streamReady: true,
    playableUrl: null,
    contextVideoUrl: "",
    error: "",
    verificationError: "",
    mismatchOverride: null,
    /** null | 'connecting' | 'streaming' */
    validationStatus: null,
    pendingVideoId: null,
    pendingThumbnail: null,
  };
}

const EMPTY_RECORD = makeRecord();

function makeCoord() {
  return {
    cloudflareStatusPollToken: 0,
    contextValidationRequestToken: 0,
    contextValidationAbortController: null,
    videoRevealInFlight: false,
    linkedProposedVideoId: null,
  };
}

function getAccountId() {
  try {
    return JSON.parse(localStorage.getItem("currentAccountID"));
  } catch {
    return null;
  }
}

function extractVideoUrlFromIframeHtml(iframeHtml) {
  if (!iframeHtml) return "";
  if (!String(iframeHtml).includes("<iframe") && !String(iframeHtml).includes("src=")) {
    return String(iframeHtml);
  }
  const srcMatch = String(iframeHtml).match(/src=\\?"([^"\\]+)/);
  if (srcMatch && srcMatch[1]) return srcMatch[1];
  const fallbackMatch = String(iframeHtml).match(/src="([^"]+)"/);
  if (fallbackMatch && fallbackMatch[1]) return fallbackMatch[1];
  return "";
}

function isInterruptedContextValidationStream(err) {
  if (!err || typeof err !== "object") return false;
  if (err.name === "TimeoutError" || err.name === "TypeError") return true;
  return IQ_STREAM_INTERRUPTED_HTTP_STATUSES.includes(err.status);
}

/**
 * ER-12337 — durable, checkpoint-keyed state + runner for the upload → Cloudflare
 * encoding → signed URL → IQ context-validation workflow.
 *
 * The runner lives here (not in CheckpointEditFormV2) so an in-flight "video is being
 * processed" survives the editor unmounting when the operator switches responsibility
 * tabs / routes. The stream, the phase machine, the resolved playable URL and the
 * validation verdict are all held per checkpoint and rehydrated on remount.
 *
 * Domain events that only the mounted editor can deliver (`refresh-checkpoint`,
 * `video-uploaded`, `video-url-ready`, `pending-video-confirm-cleared`) are routed
 * through `emitEvent`: delivered immediately while an editor is mounted, otherwise
 * queued and drained by the next mounted editor, so background completion is never
 * lost and is never delivered twice.
 */
export const useIqVideoProcessingStore = defineStore("iqVideoProcessing", () => {
  const notificationStore = useNotificationStore();
  const iqChatStore = useIQChatStore();

  // checkpointId -> record (reactive)
  const records = reactive({});
  // Vue 2.7 reactive() does not track dynamic keys; a version counter gives computeds a
  // stable dependency to re-evaluate on after set()/del().
  const version = ref(0);
  function bumpVersion() {
    version.value += 1;
  }

  // checkpointId -> non-reactive coordination (tokens / controller / in-flight flags)
  const coords = {};

  // Queued domain events for when no editor is mounted to consume them.
  const pendingEmits = [];
  /**
   * Mounted editors that can receive domain events. The last one wins, so a nested editor
   * (the read-only past-version form rendered inside CheckpointPastVersionCard) can never
   * permanently replace the live editor's emitter: unmounting it falls back to the parent.
   */
  const emitters = [];

  function currentEmitter() {
    return emitters.length ? emitters[emitters.length - 1] : null;
  }

  function keyOf(id) {
    return id == null ? null : String(id);
  }

  function ensure(id) {
    const key = keyOf(id);
    if (key == null) return EMPTY_RECORD;
    if (!records[key]) {
      set(records, key, makeRecord());
      bumpVersion();
    }
    return records[key];
  }

  function get(id) {
    // Register a reactive dependency on every record mutation.
    void version.value;
    const key = keyOf(id);
    if (key == null) return EMPTY_RECORD;
    return records[key] || EMPTY_RECORD;
  }

  function coord(id) {
    const key = keyOf(id);
    if (key == null) return makeCoord();
    if (!coords[key]) coords[key] = makeCoord();
    return coords[key];
  }

  /** True while the checkpoint has an in-flight or unresolved background workflow. */
  function hasActivity(id) {
    const r = get(id);
    return (
      r.phase != null ||
      r.revealPending === true ||
      r.mismatchOverride != null ||
      r.validationStatus != null
    );
  }

  function isBusy(id) {
    const r = get(id);
    return (
      r.phase === "processing" ||
      r.phase === "validating" ||
      r.revealPending === true ||
      r.validationStatus != null
    );
  }

  // ── domain events ──────────────────────────────────────────────────────────
  function registerEmitter(fn) {
    if (typeof fn !== "function") return;
    const existing = emitters.indexOf(fn);
    if (existing !== -1) emitters.splice(existing, 1);
    emitters.push(fn);
  }
  /**
   * Remove an emitter. Pass the function that was registered so a nested editor (e.g. the
   * read-only past-version form) cannot clear the live editor's emitter on unmount; calling
   * without an argument still clears every emitter.
   */
  function unregisterEmitter(fn) {
    if (fn == null) {
      emitters.length = 0;
      return;
    }
    const index = emitters.indexOf(fn);
    if (index !== -1) emitters.splice(index, 1);
  }
  function emitEvent(name, payload) {
    const emitter = currentEmitter();
    if (typeof emitter === "function") {
      emitter(name, payload);
      return;
    }
    pendingEmits.push({ name, payload });
  }
  /** Deliver events raised while the editor was unmounted to the now-mounted editor. */
  function drainEmits() {
    const emitter = currentEmitter();
    if (typeof emitter !== "function" || !pendingEmits.length) return;
    const queued = pendingEmits.splice(0, pendingEmits.length);
    queued.forEach(({ name, payload }) => emitter(name, payload));
  }

  // ── lifecycle helpers ──────────────────────────────────────────────────────
  function beginThinking() {
    try {
      iqChatStore.startThinking();
      iqChatStore.updateCurrentReasoning("");
    } catch {
      // Pinia may be unavailable in isolated unit tests.
    }
  }
  function updateThinking(text) {
    try {
      iqChatStore.updateCurrentReasoning(text);
    } catch {
      // Pinia may be unavailable in isolated unit tests.
    }
  }
  function endThinking() {
    try {
      iqChatStore.stopThinking();
      iqChatStore.updateCurrentReasoning("");
    } catch {
      // Pinia may be unavailable in isolated unit tests.
    }
  }

  async function notifyPeersOfCheckpointUpdate(checkpointId) {
    const accountId = getAccountId();
    if (accountId == null || checkpointId == null) return;
    try {
      const response = await notifyCheckpointPresenceUpdated(accountId, checkpointId);
      if (response?.data?.broadcast === false) {
        try {
          await notifyCheckpointPresenceUpdated(accountId, checkpointId);
        } catch {
          // Optional single retry failed — save still succeeded.
        }
      }
    } catch (error) {
      if (!error?.response || error.response.status >= 500) {
        console.error("[iqVideoProcessingStore] checkpoint-updated notify failed:", error);
      }
    }
  }

  /**
   * The context carries a snapshot of the checkpoint taken when the run started. Re-read it
   * through `getCheckpoint` when available so a late-arriving Cloudflare video id (e.g. after
   * a refresh-checkpoint) is picked up instead of the stale snapshot.
   */
  function resolveContextCheckpoint(ctx) {
    if (typeof ctx?.getCheckpoint === "function") {
      try {
        const live = ctx.getCheckpoint();
        if (live) return live;
      } catch {
        // Fall through to the snapshot.
      }
    }
    return ctx?.checkpoint || {};
  }

  function resolveCloudflareVideoUid(ctx, payload = {}) {
    const checkpoint = resolveContextCheckpoint(ctx);
    return (
      payload?.videoId ||
      payload?.video_id ||
      checkpoint.microlearning_cloudflare_video_id ||
      checkpoint.microlearning_video_id ||
      checkpoint.active_microlearning_cloudflare_video_id ||
      checkpoint.active_microlearning_video_id ||
      checkpoint.microlearning_id ||
      extractCloudflareStreamVideoUidFromEmbeds(
        payload?.signedUrl || checkpoint.microlearning || checkpoint.active_microlearning || ""
      ) ||
      null
    );
  }

  async function waitUntilCloudflareVideoReady(uid, token, checkpointId) {
    if (!uid) return false;
    const { ready } = await pollCloudflareStreamStatus({
      uid,
      pollMs: 1000,
      maxMs: 10 * 60 * 1000,
      fetcher: async (id) => {
        const res = await getCloudflareVideoStatus(id);
        return res?.data ?? null;
      },
    });
    return token === coord(checkpointId).cloudflareStatusPollToken && ready;
  }

  async function fetchPlayableSignedUrl(ctx, payload = {}) {
    const accountId = ctx?.accountId ?? getAccountId();
    if (!accountId || ctx?.responsibilityId == null || ctx?.checkpointId == null) {
      return isPlayableSignedVideoUrl(payload?.signedUrl) ? payload.signedUrl : null;
    }
    try {
      const response = await getCheckpointVideoSignedUrl(
        accountId,
        ctx.responsibilityId,
        ctx.checkpointId
      );
      const url = response?.data?.url ?? payload?.signedUrl ?? null;
      return isPlayableSignedVideoUrl(url) ? url : null;
    } catch {
      return isPlayableSignedVideoUrl(payload?.signedUrl) ? payload.signedUrl : null;
    }
  }

  /**
   * Poll video-signed-url until playable; keep the URL in the store only
   * (microlearning columns are not writable via checkpoint PATCH).
   */
  async function pollPlayableSignedUrl(ctx, token) {
    const accountId = ctx?.accountId ?? getAccountId();
    if (!accountId || ctx?.responsibilityId == null || ctx?.checkpointId == null) {
      return null;
    }
    const record = ensure(ctx.checkpointId);
    const MAX_ATTEMPTS = 30;
    const DELAY_MS = 1000;
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      if (token !== coord(ctx.checkpointId).cloudflareStatusPollToken) return null;
      try {
        const signedUrlRes = await getCheckpointVideoSignedUrl(
          accountId,
          ctx.responsibilityId,
          ctx.checkpointId
        );
        const url = signedUrlRes?.data?.url ?? null;
        if (isPlayableSignedVideoUrl(url)) {
          record.playableUrl = url;
          return url;
        }
      } catch (err) {
        console.error("[iqVideoProcessingStore] Error polling signed URL:", err);
      }
      await new Promise((resolve) => setTimeout(resolve, DELAY_MS));
    }
    return null;
  }

  /**
   * Keep the processing UI until cloudflare-video-status reports ready_to_stream, then
   * resolve the signed URL and reveal the player — no fixed reveal timer.
   */
  async function reveal(ctx, payload = {}, { showToast = true } = {}) {
    const c = coord(ctx.checkpointId);
    if (c.videoRevealInFlight) return;
    c.videoRevealInFlight = true;
    const record = ensure(ctx.checkpointId);
    record.revealPending = true;
    record.streamReady = false;
    const token = (c.cloudflareStatusPollToken += 1);
    if (showToast) {
      notificationStore.info(VIDEO_POST_UPLOAD_TOAST_TITLE, VIDEO_POST_UPLOAD_TOAST_MESSAGE, 8000);
    }

    try {
      // UID may arrive after refresh-checkpoint; wait briefly for it before polling status.
      const uidWaitDeadline = Date.now() + 30 * 1000;
      let uid = resolveCloudflareVideoUid(ctx, payload);
      while (
        !uid &&
        Date.now() < uidWaitDeadline &&
        token === coord(ctx.checkpointId).cloudflareStatusPollToken
      ) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        uid = resolveCloudflareVideoUid(ctx, payload);
      }
      if (token !== coord(ctx.checkpointId).cloudflareStatusPollToken) return;

      if (uid) {
        const ready = await waitUntilCloudflareVideoReady(uid, token, ctx.checkpointId);
        if (token !== coord(ctx.checkpointId).cloudflareStatusPollToken) return;
        // Encoding still not ready after max poll — keep processing UI, do not show player.
        if (!ready) return;
      } else {
        // Cannot confirm Cloudflare readiness without a video id — keep processing UI.
        return;
      }

      const signedUrl = await fetchPlayableSignedUrl(ctx, payload);
      if (token !== coord(ctx.checkpointId).cloudflareStatusPollToken) return;

      const readyPayload = {
        ...payload,
        signedUrl: signedUrl ?? payload?.signedUrl ?? null,
      };
      if (readyPayload.signedUrl) {
        ensure(ctx.checkpointId).playableUrl = readyPayload.signedUrl;
      }
      ensure(ctx.checkpointId).streamReady = true;
      emitEvent("refresh-checkpoint");
      emitEvent("video-url-ready", readyPayload);
    } finally {
      if (token === coord(ctx.checkpointId).cloudflareStatusPollToken) {
        ensure(ctx.checkpointId).revealPending = false;
        coord(ctx.checkpointId).videoRevealInFlight = false;
      }
    }
  }

  /** POST confirm-proposed-video (video_id + thumbnail) once per uploaded video. */
  async function linkPendingProposedVideo(ctx) {
    const accountId = ctx?.accountId ?? getAccountId();
    const record = ensure(ctx.checkpointId);
    const videoId = record.pendingVideoId;
    if (!accountId || ctx?.checkpointId == null || !videoId) return null;
    const c = coord(ctx.checkpointId);
    if (c.linkedProposedVideoId !== null && String(c.linkedProposedVideoId) === String(videoId)) {
      return null;
    }

    const formData = new FormData();
    formData.append("video_id", videoId);
    if (record.pendingThumbnail) {
      formData.append("thumbnail", record.pendingThumbnail, "thumbnail.jpg");
    }
    const confirmRes = await confirmProposedVideo(accountId, ctx.checkpointId, formData);
    c.linkedProposedVideoId = videoId;
    return confirmRes.data ?? {};
  }

  async function confirmPendingProposedVideo(ctx) {
    const confirmData = await linkPendingProposedVideo(ctx);
    emitEvent("pending-video-confirm-cleared");
    return confirmData;
  }

  function resolveContextValidationStreamVideoUid(ctx, videoUrl) {
    const record = get(ctx.checkpointId);
    return (
      normalizeCloudflareStreamVideoUid(record.pendingVideoId) ||
      extractCloudflareStreamVideoUidFromEmbeds(videoUrl) ||
      normalizeCloudflareStreamVideoUid(
        resolveContextCheckpoint(ctx)?.microlearning_cloudflare_video_id
      )
    );
  }

  function buildContextValidationUserPrompt(ctx, extraPromptSuffix = "") {
    const checkpointInfo = [ctx?.prefix, ctx?.criteria].filter(Boolean).join(" ");
    const responsibilityInfo = [ctx?.responsibilityCode, ctx?.responsibilityName]
      .filter(Boolean)
      .join(" ");
    const base = `Pls suggest Inspection instructions from video of Checkpoint: ${checkpointInfo} belonging to responsibility ${responsibilityInfo}`;
    return extraPromptSuffix ? `${base} ${extraPromptSuffix}` : base;
  }

  /** Direct POST /video_to_components/ while the thinking banner is visible. */
  async function runContextValidationIqRequest(ctx) {
    const c = coord(ctx.checkpointId);
    const token = (c.contextValidationRequestToken += 1);
    if (c.contextValidationAbortController) {
      c.contextValidationAbortController.abort();
    }
    c.contextValidationAbortController = new AbortController();
    const record = ensure(ctx.checkpointId);
    record.validationStatus = "connecting";

    const iqBaseUrl = configService.get("iqBaseUrl");
    const apiEndpoint = buildIqVideoSuggestEndpoint(iqBaseUrl, true);
    const currentAccountDetails = JSON.parse(localStorage.getItem("currentAccountDetails") || "{}");
    const businessPurpose = currentAccountDetails.purpose || "";
    const validationVideoUrl = record.contextVideoUrl || ctx.videoPassChapterVideoUrl || "";
    const streamVideoUid = resolveContextValidationStreamVideoUid(ctx, validationVideoUrl);
    const extractedVideoUrl =
      cloudflareStreamIframeSrc(streamVideoUid) ||
      extractVideoUrlFromIframeHtml(validationVideoUrl) ||
      validationVideoUrl;
    const checkpointCriteria = `${ctx.prefix || ""} ${ctx.criteria || ""}`.trim();
    const customPayload = buildVideoToComponentsPayload({
      videoUrl: extractedVideoUrl,
      businessPurpose,
      responsibilityName: ctx.responsibilityName || "",
      responsibilityPurpose: ctx.responsibilityPurpose || "",
      checkpointCriteria,
      language: "english",
      microlearningSelection: null,
    });

    try {
      let completeData = null;
      await askIQStream({
        question: buildContextValidationUserPrompt(
          ctx,
          "Validate whether this video matches the checkpoint context."
        ),
        k: 5,
        signal: c.contextValidationAbortController.signal,
        timeoutMs: IQ_STREAM_TIMEOUT_MS,
        customEndpoint: apiEndpoint,
        customPayload,
        onThinkingUpdate: (text) => {
          if (ensure(ctx.checkpointId).validationStatus === "connecting") {
            ensure(ctx.checkpointId).validationStatus = "streaming";
          }
          updateThinking(text);
        },
        onComplete: (data) => {
          completeData = data;
        },
        onError: () => {
          // The check itself could not run — route to verification-failed (distinct from
          // a mismatch verdict); Generate must stay blocked.
          if (ensure(ctx.checkpointId).phase === "validating") {
            ensure(ctx.checkpointId).phase = "verification-failed";
            ensure(ctx.checkpointId).verificationError = VERIFICATION_FAILED_DEFAULT_ERROR;
            ensure(ctx.checkpointId).validationStatus = null;
            endThinking();
          }
        },
      });

      if (token !== coord(ctx.checkpointId).contextValidationRequestToken) return;
      // Stream is done — freeze the banner on its last thinking line while confirming.
      try {
        iqChatStore.stopThinking();
      } catch {
        // Pinia may be unavailable in isolated unit tests.
      }
      ensure(ctx.checkpointId).validationStatus = null;
      if (ensure(ctx.checkpointId).phase !== "validating") return;

      const fallbackVideoUrl =
        extractVideoUrlFromIframeHtml(ensure(ctx.checkpointId).contextVideoUrl) ||
        ensure(ctx.checkpointId).contextVideoUrl ||
        null;
      const suggestedInstructions = buildContextValidationSuggestedInstructions(
        completeData,
        fallbackVideoUrl
      );
      await handleContextValidationOpenReview(ctx, { suggestedInstructions });
    } catch (err) {
      if (err?.name === "AbortError" || err?.name === "CanceledError") return;
      if (token !== coord(ctx.checkpointId).contextValidationRequestToken) return;
      console.error("[iqVideoProcessingStore] context validation IQ request failed:", err);
      ensure(ctx.checkpointId).phase = "verification-failed";
      ensure(ctx.checkpointId).verificationError = isInterruptedContextValidationStream(err)
        ? VIDEO_CONTEXT_STREAM_INTERRUPTED_ERROR
        : VERIFICATION_FAILED_DEFAULT_ERROR;
      ensure(ctx.checkpointId).validationStatus = null;
    } finally {
      if (token === coord(ctx.checkpointId).contextValidationRequestToken) {
        coord(ctx.checkpointId).contextValidationAbortController = null;
        endThinking();
        if (ensure(ctx.checkpointId).phase !== "validating") {
          ensure(ctx.checkpointId).validationStatus = null;
        }
      }
    }
  }

  /** PASS after replace must clear a prior FAIL persist, else the mismatch screen sticks. */
  async function clearPersistedContextValidationMismatch(ctx) {
    const accountId = ctx?.accountId ?? getAccountId();
    if (!accountId || ctx?.responsibilityId == null || ctx?.checkpointId == null) return false;
    try {
      const patch = buildClearContextValidationMismatchPatch(ctx?.proposedInstructions);
      await changeCheckpointV2(accountId, ctx.responsibilityId, ctx.checkpointId, patch);
      void notifyPeersOfCheckpointUpdate(ctx.checkpointId);
      return true;
    } catch (err) {
      console.error("[iqVideoProcessingStore] clear persisted video mismatch:", err);
      return false;
    }
  }

  async function persistVideoToComponentsResult(ctx, suggestedInstructions, mode) {
    const accountId = ctx?.accountId ?? getAccountId();
    if (!accountId || ctx?.responsibilityId == null || ctx?.checkpointId == null) return false;
    try {
      let patch = buildVideoToComponentsCheckpointPatch({
        suggestedInstructions,
        mode,
        microlearningSelection: ctx.selection,
      });

      if (mode === "fail") {
        const videoUrl =
          suggestedInstructions?.video_url ??
          (extractVideoUrlFromIframeHtml(String(ctx.displayVideoUrl || "")) || null);
        if (videoUrl && patch.proposed_instructions) {
          const parsed = JSON.parse(patch.proposed_instructions);
          patch = {
            ...patch,
            proposed_instructions: JSON.stringify({ ...parsed, video_url: videoUrl }),
          };
        }
      }

      await changeCheckpointV2(accountId, ctx.responsibilityId, ctx.checkpointId, patch);

      if (mode === "pass" && store.getters["featureFlags/ideaMDP427"]) {
        await saveVideoInspectionInstructions(accountId, ctx.responsibilityId, ctx.checkpointId, {
          inspection_instructions: suggestedInstructions,
        });
      }

      void notifyPeersOfCheckpointUpdate(ctx.checkpointId);
      emitEvent("refresh-checkpoint");
      return true;
    } catch (err) {
      console.error("[iqVideoProcessingStore] persist video_to_components result:", err);
      notificationStore.error(
        err?.response?.data?.detail ??
          err?.response?.data?.message ??
          "Could not save inspection draft."
      );
      return false;
    }
  }

  async function handleContextValidationOpenReview(ctx, { suggestedInstructions }) {
    const record = ensure(ctx.checkpointId);
    const isFail = isContextValidationFail(suggestedInstructions);
    const isVerificationFailed = isContextVerificationFailed(suggestedInstructions);

    try {
      // confirm-proposed-video only AFTER video_to_components has returned.
      await confirmPendingProposedVideo(ctx);

      if (isVerificationFailed) {
        record.verificationError =
          suggestedInstructions?.error || VERIFICATION_FAILED_DEFAULT_ERROR;
        record.mismatchOverride = false;
        record.phase = "verification-failed";
        return;
      }

      if (isFail) {
        const withError = {
          ...suggestedInstructions,
          error:
            suggestedInstructions?.error ??
            suggestedInstructions?.validation?.reasoning ??
            suggestedInstructions?.inspection_instructions?.validation?.reasoning ??
            "This video does not match the context of the checkpoint.",
          video_url:
            suggestedInstructions?.video_url ||
            extractVideoUrlFromIframeHtml(record.contextVideoUrl) ||
            record.contextVideoUrl,
        };
        record.error = withError.error;
        record.mismatchOverride = true;
        // Always show mismatch UI on FAIL — do not wait on / gate behind persist.
        record.phase = "mismatch";
        const saved = await persistVideoToComponentsResult(ctx, withError, "fail");
        if (!saved) notificationStore.error("Could not save video validation result.");
        emitEvent("refresh-checkpoint");
        return;
      }

      // PASS — video confirmed; show player + Generate bar (do not auto-apply generation).
      record.mismatchOverride = false;
      record.phase = null;
      record.error = "";
      record.pendingThumbnail = null;
      const saved = await clearPersistedContextValidationMismatch(ctx);
      if (!saved) notificationStore.error("Could not save video validation result.");
      emitEvent("video-uploaded", {
        videoId: record.pendingVideoId,
        checkpointData: { video_id: record.pendingVideoId },
      });
      emitEvent("refresh-checkpoint");
      if (record.playableUrl) {
        record.streamReady = true;
        record.revealPending = false;
        emitEvent("video-url-ready", {
          videoId: record.pendingVideoId,
          signedUrl: record.playableUrl,
        });
      } else {
        await reveal(ctx, { videoId: record.pendingVideoId, signedUrl: null }, { showToast: false });
      }
    } catch (err) {
      console.error("[iqVideoProcessingStore] confirm after context validation failed:", err);
      record.phase = null;
      notificationStore.error(
        err?.response?.data?.detail ??
          err?.response?.data?.message ??
          "Could not confirm uploaded video."
      );
    }
  }

  /**
   * UploadVideoWizard finished TUS — link, wait for encoding, resolve signed URL, then
   * run context validation. Survives editor unmount: all state lives on the record.
   */
  async function startPostUploadValidation(ctx, { videoId, thumbnail = null } = {}) {
    const record = ensure(ctx.checkpointId);
    record.pendingVideoId = videoId;
    record.pendingThumbnail = thumbnail ?? null;
    record.contextVideoUrl = cloudflareStreamIframeHtml(videoId);
    record.playableUrl = null;
    record.streamReady = false;
    record.revealPending = true;
    record.phase = "processing";

    const c = coord(ctx.checkpointId);
    const token = (c.cloudflareStatusPollToken += 1);
    try {
      const linked = await linkPendingProposedVideo(ctx);
      if (token !== coord(ctx.checkpointId).cloudflareStatusPollToken) return;
      if (linked) emitEvent("refresh-checkpoint");

      const ready = await waitUntilCloudflareVideoReady(videoId, token, ctx.checkpointId);
      if (token !== coord(ctx.checkpointId).cloudflareStatusPollToken) return;
      // Encoding did not finish within the poll window — keep the processing UI.
      if (!ready) return;

      const signedUrl = await pollPlayableSignedUrl(ctx, token);
      if (token !== coord(ctx.checkpointId).cloudflareStatusPollToken) return;
      if (!signedUrl) {
        notificationStore.error("Could not get a playable video URL. Please try again.");
        ensure(ctx.checkpointId).revealPending = false;
        ensure(ctx.checkpointId).phase = null;
        return;
      }

      // Playable URL ready — show thinking banner and call video_to_components directly.
      ensure(ctx.checkpointId).contextVideoUrl = signedUrl;
      ensure(ctx.checkpointId).revealPending = false;
      ensure(ctx.checkpointId).streamReady = true;
      beginThinking();
      ensure(ctx.checkpointId).validationStatus = "connecting";
      ensure(ctx.checkpointId).phase = "validating";
      void runContextValidationIqRequest(ctx);
    } catch (err) {
      console.error("[iqVideoProcessingStore] post-upload context validation prep failed:", err);
      ensure(ctx.checkpointId).revealPending = false;
      ensure(ctx.checkpointId).phase = null;
      notificationStore.error("Could not prepare video for validation. Please try again.");
    }
  }

  /** Mismatch → Try Again: re-run video_to_components against the current video + criteria. */
  function retryContextValidation(ctx) {
    beginThinking();
    const record = ensure(ctx.checkpointId);
    record.phase = "validating";
    record.validationStatus = "connecting";
    record.error = "";
    record.verificationError = "";
    record.mismatchOverride = null;
    void runContextValidationIqRequest(ctx);
  }

  /**
   * Forget a checkpoint's workflow without emitting domain events — for the "this checkpoint
   * is no longer a video" case, not a navigation. Navigation must NEVER call this.
   */
  function reset(checkpointId) {
    const key = keyOf(checkpointId);
    if (key == null) return;
    const c = coord(key);
    c.cloudflareStatusPollToken += 1;
    c.contextValidationRequestToken += 1;
    if (c.contextValidationAbortController) {
      try {
        c.contextValidationAbortController.abort();
      } catch {
        // AbortController may be unavailable in isolated unit tests.
      }
      c.contextValidationAbortController = null;
    }
    c.videoRevealInFlight = false;
    c.linkedProposedVideoId = null;
    set(records, key, makeRecord());
    bumpVersion();
    endThinking();
  }

  /**
   * Explicit cancel (Exit Edit / Cancel and Discard / lock loss): abort polls + the IQ
   * stream and forget the checkpoint's workflow. Navigation must NOT call this.
   */
  function abort(checkpointId) {
    const key = keyOf(checkpointId);
    if (key == null) return;
    const c = coord(key);
    c.cloudflareStatusPollToken += 1;
    c.contextValidationRequestToken += 1;
    if (c.contextValidationAbortController) {
      try {
        c.contextValidationAbortController.abort();
      } catch {
        // AbortController may be unavailable in isolated unit tests.
      }
      c.contextValidationAbortController = null;
    }
    c.videoRevealInFlight = false;
    c.linkedProposedVideoId = null;
    set(records, key, makeRecord());
    bumpVersion();
    endThinking();
    emitEvent("pending-video-confirm-cleared");
  }

  /** Session teardown (logout / account switch): cancel everything and drop all state. */
  function clearAll() {
    Object.keys(coords).forEach((key) => {
      const c = coords[key];
      c.cloudflareStatusPollToken += 1;
      c.contextValidationRequestToken += 1;
      if (c.contextValidationAbortController) {
        try {
          c.contextValidationAbortController.abort();
        } catch {
          // AbortController may be unavailable in isolated unit tests.
        }
        c.contextValidationAbortController = null;
      }
    });
    Object.keys(records).forEach((key) => del(records, key));
    Object.keys(coords).forEach((key) => delete coords[key]);
    pendingEmits.length = 0;
    bumpVersion();
    endThinking();
  }

  return {
    records,
    version,
    get,
    ensure,
    coord,
    hasActivity,
    isBusy,
    registerEmitter,
    unregisterEmitter,
    emitEvent,
    drainEmits,
    reveal,
    startPostUploadValidation,
    retryContextValidation,
    confirmPendingProposedVideo,
    linkPendingProposedVideo,
    reset,
    abort,
    clearAll,
  };
});
